import { useMemo, useState } from "react";
import {
  CreditCardIcon,
  DownloadIcon,
  LandmarkIcon,
  MailIcon,
} from "lucide-react";

import {
  DocumentEnum,
  DocumentPaymentMethodEnum,
  DocumentPaymentScheduleStatusEnum,
  PrintedLineItemFragment,
  PublicDocumentFragment,
} from "@/__generated__/graphql";
import { AnimatedTabs } from "@/components/atoms/AnimatedTabs";
import { Icon } from "@/components/atoms/Icon";
import { icons } from "@/objects/@icons";
import { Popper } from "@/components/atoms/Popper";
import { PdfPreview } from "@/components/molecules/PdfPreview";
import { StripePaymentIntentFlow } from "@/components/molecules/StripePaymentIntentFlow";
import {
  PublicDocumentViewPaymentSchedule,
  type PublicDocumentPaymentSchedule,
} from "@/app/public/[id]/_page/views/PublicDocumentView/PublicDocumentViewPaymentSchedule";
import { DocumentPreview } from "@/components/organisms/DocumentPreview";
import { useInvoicePdf } from "@/components/organisms/DocumentPreview/DocumentPreviewInvoicePDF";
import { describeInvoice } from "@/components/organisms/DocumentPreview/DocumentPreview.utils";
import { List } from "@/components/organisms/List";
import { useDisabledOverscroll } from "@/hooks/useDisabledOverscroll";
import { State } from "@/state";
import { Utils } from "@/utils";
import { Button } from "@/components/ui/button";
import {
  ButtonGroup,
  ButtonGroupSeparator,
} from "@/components/ui/button-group";
import { Skeleton } from "@/components/ui/skeleton";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";

type FileNameOption = {
  id: string;
  label: string;
  filename: string;
};

function DownloadButtonGroup({
  fileNameOptions,
  handleDownloadPDF,
  isLoading,
  document,
  publicUrl,
}: {
  fileNameOptions: FileNameOption[];
  /** Saves the file the page shows; ``null`` where it shows none to save. */
  handleDownloadPDF: ((filename?: string) => void) | null;
  /** The file still being drawn. */
  isLoading: boolean;
  document: PublicDocumentFragment;
  publicUrl: string;
}) {
  const [isPopperOpen, setIsPopperOpen] = useState(false);

  const handleEmail = () => {
    const invoiceRef = document?.reference || document?.name || "Invoice";
    const entityName = document?.entity?.name || "Our Company";
    const amountDue = Number(document?.amountDue ?? document?.amountTotal ?? 0);
    const currency = document?.amountCurrency?.toUpperCase() || "CAD";

    const subject = encodeURIComponent(
      `Invoice ${invoiceRef} from ${entityName}`,
    );
    const body = encodeURIComponent(
      `Hi,\n\nInvoice ${invoiceRef} for ${currency} ${amountDue.toFixed(2)} is ready to view and pay online:\n${publicUrl}\n\nThank you for your business.\n\n`,
    );
    // To the contact it is for, by the link its page is read at.
    const to = encodeURIComponent(document?.contact?.emailAddress ?? "");

    window.location.href = `mailto:${to}?subject=${subject}&body=${body}`;
  };

  return (
    <div className="flex justify-end w-180 max-w-full mb-2 gap-2">
      <Button variant="secondary" size="sm" onClick={handleEmail}>
        <Icon ReactIcon={MailIcon} />
        Email
      </Button>

      {/* Download button group: the file the page shows, once it is drawn -
          a document shown as no file has none to save. */}
      {handleDownloadPDF && (
        <Skeleton
          loading={isLoading}
          variant="rectangular"
          className="w-fit max-w-full flex rounded-md"
        >
          <ButtonGroup>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => handleDownloadPDF()}
            >
              <Icon ReactIcon={DownloadIcon} />
              Download
            </Button>
            {/* A group of buttons that are not outlined is divided, as shadcn's are. */}
            <ButtonGroupSeparator />
            {/* Chevron dropdown */}
            <Popper
              placement="bottom-end"
              isOpenState={[isPopperOpen, setIsPopperOpen]}
              floatingStyle={{ padding: 0 }}
              anchorLayout="button-group"
              anchor={
                <Button
                  variant="secondary"
                  size="icon-sm"
                  aria-label="Download as"
                >
                  <icons.chevronDown />
                </Button>
              }
            >
              <div className="min-w-70 p-1">
                <Typography
                  level="body-xs"
                  className="pl-2 pr-2 pt-1 pb-1 text-muted-foreground/80"
                >
                  Download as
                </Typography>
                <List<FileNameOption>
                  data={fileNameOptions}
                  getRowKey={(option) => option.id}
                  onItemClick={(option) => {
                    setIsPopperOpen(false);
                    handleDownloadPDF(option.filename);
                  }}
                  size="sm"
                  autofocus
                >
                  <List.Items<FileNameOption>>
                    {(option) => (
                      <List.Item<FileNameOption> key={option.id} value={option}>
                        <Typography level="body-md">
                          {option.label}.pdf
                        </Typography>
                      </List.Item>
                    )}
                  </List.Items>
                </List>
              </div>
            </Popper>
          </ButtonGroup>
        </Skeleton>
      )}
    </div>
  );
}

/** The page's node: base PublicDocument fields plus the separately queried lines and instalments. */
export type PublicDocumentPage = PublicDocumentFragment & {
  lineItems?: PrintedLineItemFragment[] | null;
  paymentSchedule?: PublicDocumentPaymentSchedule | null;
};

export function PublicDocumentView({
  data,
  isLoading,
  refetch,
}: {
  data: PublicDocumentPage;
  isLoading: boolean;
  refetch?: () => void;
}) {
  useDisabledOverscroll();
  // The page's own address: the shared document's id, which is what it is
  // read by - never the bare identifier, which reads as no page at all.
  const publicUrl = `${window.location.origin}/public/${data?.id}`;
  // An invoice is shown as its PDF - worded as its business's canvas is - and
  // that same file is what Download saves: the page cannot show one thing and
  // hand over another.
  const invoice = useMemo(
    () =>
      data?.documentType === DocumentEnum.Invoice
        ? describeInvoice({
            document: data,
            lines: data.lineItems ?? [],
            template: data.template,
            canAcceptPayments: Boolean(data.canAcceptPayments),
            publicUrl,
          })
        : null,
    [data, publicUrl],
  );
  const file = useInvoicePdf(invoice, data?.template);
  // The ways it is paid, as the invoice says: by card where the business
  // takes cards through Stripe, by e-transfer as its template tells the payer.
  const paymentTabs = [
    ...(data?.canAcceptPayments ? ["card"] : []),
    ...((data?.paymentMethods ?? []).includes(
      DocumentPaymentMethodEnum.ETransfer,
    )
      ? ["e-transfer"]
      : []),
  ];
  const [chosenPaymentMethod, setSelectedPaymentMethod] = useState<
    string | null
  >(null);
  const selectedPaymentMethod =
    chosenPaymentMethod && paymentTabs.includes(chosenPaymentMethod)
      ? chosenPaymentMethod
      : paymentTabs[0];

  // An invoice paid on a schedule is paid one instalment at a time, so the
  // page charges the selected instalment rather than the whole invoice. The
  // first unpaid one is preselected — it is what a payer almost always wants,
  // and it keeps the card form from rendering with nothing chosen.
  const schedule = useMemo(
    () => data?.paymentSchedule ?? [],
    [data?.paymentSchedule],
  );
  const firstUnpaidIdentifier = useMemo(
    () =>
      schedule.find(
        (item) => item.status === DocumentPaymentScheduleStatusEnum.Pending,
      )?.identifier ?? null,
    [schedule],
  );
  const [selectedItemIdentifier, setSelectedItemIdentifier] = useState<
    string | null
  >(null);
  const activeItemIdentifier = selectedItemIdentifier ?? firstUnpaidIdentifier;
  const activeItem = schedule.find(
    (item) => item.identifier === activeItemIdentifier,
  );

  // Check if the current user owns this document's entity
  const activeEntityIdentifiers = State(
    (state) => state.activeEntityIdentifiers,
  );
  const documentEntityIdentifier = data?.entity?.identifier;
  const isCreatorViewing = useMemo(
    () =>
      documentEntityIdentifier &&
      activeEntityIdentifiers.includes(documentEntityIdentifier),
    [activeEntityIdentifiers, documentEntityIdentifier],
  );

  // Only the entity that issued the invoice is asked to connect Stripe. The
  // predicate was already computed here but never used, so every payer was
  // being pitched onboarding for somebody else's account — harmless while the
  // button went nowhere, misleading now that it opens the OAuth flow.
  const showStripeSetupPrompt = Boolean(
    isCreatorViewing && !data?.canAcceptPayments,
  );

  // Format date for filename
  const formatDateForFilename = (
    dateStr: string | null | undefined,
  ): string => {
    if (!dateStr) return new Date().toISOString().split("T")[0];
    return new Date(dateStr).toISOString().split("T")[0];
  };

  // File naming options
  const fileNameOptions = useMemo(() => {
    const reference = data?.reference || data?.name || "invoice";
    const entityName =
      data?.entity?.name?.replace(/[^a-zA-Z0-9]/g, "-") || "company";
    const dateIssued = formatDateForFilename(data?.dateIssued);
    const contactName =
      data?.contact?.organizationName?.replace(/[^a-zA-Z0-9]/g, "-") ||
      "customer";

    return [
      {
        id: "reference",
        label: `Invoice-${reference}`,
        filename: `Invoice-${reference}.pdf`,
      },
      {
        id: "date",
        label: `Invoice-${dateIssued}`,
        filename: `Invoice-${dateIssued}.pdf`,
      },
      {
        id: "entity-reference",
        label: `${entityName}-Invoice-${reference}`,
        filename: `${entityName}-Invoice-${reference}.pdf`,
      },
      {
        id: "contact-reference",
        label: `${contactName}-Invoice-${reference}`,
        filename: `${contactName}-Invoice-${reference}.pdf`,
      },
      {
        id: "full",
        label: `${entityName}-${contactName}-${dateIssued}`,
        filename: `${entityName}-${contactName}-${dateIssued}.pdf`,
      },
    ];
  }, [data]);

  // The file the page shows, under the name picked.
  const handleDownloadPDF = (filename?: string) => {
    if (!file) return;
    const link = document.createElement("a");
    link.href = file.url;
    link.download = filename || fileNameOptions[0].filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="flex flex-row-reverse justify-center h-screen max-w-[100vw] max-h-screen overflow-y-auto overflow-x-hidden relative items-center flex-nowrap">
      <div className="w-[50%] h-full bg-muted flex flex-col justify-start items-start [border-left-width:1px] [border-left-style:solid] border-border p-8">
        {/* Show Stripe setup prompt for creators without payments enabled */}
        {showStripeSetupPrompt && (
          <div className="flex flex-col justify-start items-start w-180 max-w-full bg-background rounded-md [border-width:1px] [border-style:solid] border-border p-6 gap-4">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-sm bg-accent text-primary">
                <CreditCardIcon size={20} />
              </div>
              <Typography level="title-lg">
                Accept payments for this invoice
              </Typography>
            </div>

            <Typography level="body-md" className="text-muted-foreground">
              Connect your Stripe account to accept credit card payments
              directly from your customers. Once enabled, customers visiting
              this page will be able to pay instantly.
            </Typography>

            <Button
              onClick={() => {
                window.location.href = "/app?connect=stripe";
              }}
              className="mt-2"
            >
              Set up Stripe payments
            </Button>
          </div>
        )}

        {/* The schedule stands on its own: a payer can read it and report an
            off-Stripe payment whether or not card payments are switched on. */}
        {schedule.length > 0 && (
          <div className="w-180 max-w-full mb-4">
            <PublicDocumentViewPaymentSchedule
              schedule={schedule}
              currency={data?.amountCurrency?.toUpperCase() || "CAD"}
              canPay={Boolean(data?.canAcceptPayments)}
              selectedItemIdentifier={activeItemIdentifier}
              onSelectItem={setSelectedItemIdentifier}
              onReported={() => refetch?.()}
            />
          </div>
        )}

        {/* Payment methods for customers */}
        {paymentTabs.length > 0 && (
          <div className="flex flex-col justify-start items-start w-180 max-w-full gap-4">
            {/* Amount due header */}
            <div className="w-full">
              <Typography level="body-sm" className="text-muted-foreground">
                <Skeleton loading={isLoading}>
                  {activeItem ? "Amount due now" : "Amount due"}
                </Skeleton>
              </Typography>
              <Typography level="h2">
                <Skeleton loading={isLoading}>
                  {Utils.fCurrency(
                    activeItem
                      ? Number(activeItem.amount ?? 0)
                      : (data?.amountDue ?? data?.amountTotal ?? 0),
                  )}{" "}
                  {data?.amountCurrency?.toUpperCase() || "CAD"}
                </Skeleton>
              </Typography>
              {activeItem && (
                <Typography
                  level="body-xs"
                  className="text-muted-foreground/80"
                >
                  {activeItem.name || "Instalment"} of{" "}
                  {Utils.fCurrency(Number(data?.amountTotal ?? 0))} total
                </Typography>
              )}
            </div>

            {/* Payment method tabs */}
            <div className="w-full">
              <AnimatedTabs
                tabs={[
                  {
                    id: "card",
                    label: (
                      <div className="flex items-center gap-1.5">
                        <CreditCardIcon size={14} />
                        Card
                      </div>
                    ),
                  },
                  {
                    id: "e-transfer",
                    label: (
                      <div className="flex items-center gap-1.5">
                        <LandmarkIcon size={14} />
                        E-transfer
                      </div>
                    ),
                  },
                ].filter((tab) => paymentTabs.includes(tab.id))}
                selectedTabId={selectedPaymentMethod}
                onTabChange={setSelectedPaymentMethod}
              />
            </div>

            {/* Card payment content */}
            {selectedPaymentMethod === "card" && (
              <div className="w-full bg-background rounded-sm [border-width:1px] [border-style:solid] border-border">
                <StripePaymentIntentFlow
                  node={data}
                  amount={
                    activeItem
                      ? Number(activeItem.amount ?? 0)
                      : parseFloat(data.amountTotal.toString())
                  }
                  currency={data.amountCurrency || "usd"}
                  paymentScheduleItemIdentifier={activeItemIdentifier}
                  onSuccess={() => {
                    setSelectedItemIdentifier(null);
                    refetch?.();
                  }}
                />
              </div>
            )}

            {/* E-transfer: as the invoice's template tells its payer. */}
            {selectedPaymentMethod === "e-transfer" && (
              <div
                data-testid="public-pay-e-transfer"
                className="w-full bg-background rounded-sm [border-width:1px] [border-style:solid] border-border p-4"
              >
                <Typography level="title-md" className="mb-4">
                  Pay by e-transfer
                </Typography>
                <div className="flex flex-col gap-3">
                  <Typography
                    level="body-md"
                    className={cn(
                      "whitespace-pre-wrap",
                      data?.template?.paymentInstructions
                        ? "text-foreground"
                        : "text-muted-foreground/80",
                    )}
                  >
                    {data?.template?.paymentInstructions ||
                      `No e-transfer details on this invoice: ask ${data?.entity?.name} for them.`}
                  </Typography>
                  {(data?.reference || data?.name) && (
                    <div>
                      <Typography
                        level="body-xs"
                        className="text-muted-foreground/80"
                      >
                        Include with your payment
                      </Typography>
                      <Typography level="body-md" className="font-mono">
                        {data.reference || data.name}
                      </Typography>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <div className="flex flex-col justify-start items-end w-[50%] h-full p-8 bg-accent overflow-y-auto">
        {/* Download button group */}
        <DownloadButtonGroup
          fileNameOptions={fileNameOptions}
          handleDownloadPDF={invoice ? handleDownloadPDF : null}
          isLoading={!file}
          document={data}
          publicUrl={publicUrl}
        />

        <div className="flex flex-col justify-end items-end w-180 max-w-full [border-width:1px] [border-style:solid] border-border rounded-md bg-muted shadow-sm overflow-hidden">
          {invoice ? (
            <PdfPreview url={file?.url ?? null} />
          ) : (
            <DocumentPreview
              document={data}
              isLoading={isLoading}
              publicUrl={publicUrl}
              template={data?.template}
            />
          )}
        </div>
      </div>
    </div>
  );
}
